// [claude-code 2026-05-15] Shell-first CLI and rich footer toolbar integration.
// [Codex 2026-07-14] S138 keeps browser controls ordered and source-owned.
import { useEffect, useState } from "react";
import { useErrorLog } from "../../hooks/useErrorLog";
import { useRiskFlow } from "../../contexts/RiskFlowContext";
import { FooterDrawer } from "./footer-toolbar/FooterDrawer";
import { FooterStrip } from "./footer-toolbar/FooterStrip";
import {
  FOOTER_BODY_FONT_STYLE,
  type FooterPanelTab,
  type FooterToolbarProps,
} from "./footer-toolbar/footer-toolbar-model";
import { useFooterTerminal } from "./footer-toolbar/use-footer-terminal";
import { setRiskFlowAlertSurfaceActive } from "../../lib/riskflow-alert-surface";

export function FooterToolbar({
  compactLevel = 0,
  topStepXEnabled = false,
  primaryPlatform = "topstepx",
  secondaryPlatform = "research",
  splitViewEnabled = false,
  allowSplitView = false,
  allowCustomIframes = true,
  ...callbacks
}: FooterToolbarProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FooterPanelTab>("terminal");
  const [updateInstalling, setUpdateInstalling] = useState(false);
  const { errorCount } = useErrorLog();
  const { fetchStatus, refreshing } = useRiskFlow();
  const terminal = useFooterTerminal(panelOpen, activeTab, setActiveTab);

  useEffect(() => {
    const handler = () => setUpdateInstalling(true);
    window.addEventListener("fintheon:update-installing", handler);
    return () =>
      window.removeEventListener("fintheon:update-installing", handler);
  }, []);
  useEffect(() => {
    const toggle = () => setPanelOpen((value) => !value);
    window.addEventListener("fintheon:toggle-footer-panel", toggle);
    return () =>
      window.removeEventListener("fintheon:toggle-footer-panel", toggle);
  }, []);
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("fintheon:footer-panel-state", {
        detail: { open: panelOpen },
      }),
    );
  }, [panelOpen]);
  useEffect(() => {
    setRiskFlowAlertSurfaceActive(
      "terminal",
      panelOpen && activeTab === "terminal",
    );
    return () => setRiskFlowAlertSurfaceActive("terminal", false);
  }, [activeTab, panelOpen]);

  const openTab = (tab: FooterPanelTab) => {
    if (panelOpen && activeTab === tab) setPanelOpen(false);
    else {
      setActiveTab(tab);
      setPanelOpen(true);
    }
  };
  const browserProps: FooterToolbarProps = {
    ...callbacks,
    allowCustomIframes,
    allowSplitView,
    compactLevel,
    primaryPlatform,
    secondaryPlatform,
    splitViewEnabled,
    topStepXEnabled,
  };

  return (
    <div
      className="fintheon-footer-surface fintheon-power-frame-surface relative flex-shrink-0 bg-[var(--fintheon-surface)]"
      style={FOOTER_BODY_FONT_STYLE}
    >
      <FooterDrawer
        activeTab={activeTab}
        errorCount={errorCount}
        open={panelOpen}
        setActiveTab={setActiveTab}
        terminal={terminal}
      />
      <FooterStrip
        {...browserProps}
        activeTab={activeTab}
        errorCount={errorCount}
        fetchStatus={fetchStatus}
        openPanel={panelOpen}
        openTab={openTab}
        refreshing={refreshing}
        updateInstalling={updateInstalling}
      />
    </div>
  );
}
